import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useOfflineQueue, useOnlineStatus } from "../../domains/field/hooks";
import { clearSession, getUser } from "../../domains/auth/session";
import { IconChevronLeft, IconOffline, IconSpinner, IconWarning } from "./icons";
import { FieldProcessStepper, stepsFromActiveIndex } from "./FieldProcessStepper";

/**
 * FieldShell — mobile-first shell for field/rep journeys with journey chrome,
 * separated from the internal admin AppShell per FRONTEND_ARCHITECTURE_STANDARDS §1.6.
 * Dark shell header (--pg-shell-bg #0F172A) per approved designs; no sidebar;
 * 360px baseline, bottom-friendly actions; offline banner + sync state.
 */

export type FieldFlow = "leads" | "payments" | "install" | "hub";

export interface FieldJourneyState {
  flow: FieldFlow;
  /** 0-based step index within the flow; ignored for hub */
  step?: number;
  /** Explicit back target; if omitted, use hub for that flow */
  backTo?: string;
  /** Hide back button (hub screens) */
  hideBack?: boolean;
}

interface FieldHeaderState {
  title: string;
  subtitle?: string;
  crumbs?: { label: string; to?: string }[];
}

interface FieldShellState {
  header: FieldHeaderState;
  journey?: FieldJourneyState;
}

const FieldHeaderContext = createContext<(h: FieldHeaderState) => void>(() => {});
const FieldJourneyContext = createContext<(j: FieldJourneyState) => void>(() => {});

/** Called by each field screen to set the dark shell header. */
export function useFieldHeader(title: string, subtitle?: string) {
  const setHeader = useContext(FieldHeaderContext);
  useEffect(() => {
    setHeader({ title, subtitle });
  }, [title, subtitle, setHeader]);
}

/** Called by each field screen to set journey navigation state. */
export function useFieldJourney(journey: FieldJourneyState) {
  const setJourney = useContext(FieldJourneyContext);
  const { flow, step, backTo, hideBack } = journey;
  useEffect(() => {
    setJourney({ flow, step, backTo, hideBack });
  }, [flow, step, backTo, hideBack, setJourney]);
}

/** Exported for tests / docs — local Mode B labels. */
export const FIELD_STEP_LABELS: Record<FieldFlow, string[]> = {
  leads: ["Capture", "Duplicates", "Review", "KYC"],
  payments: ["Record", "Verify", "Allocate", "Token", "Timeline"],
  install: ["Queue", "Scan", "Evidence", "Payment", "Submit"],
  hub: [],
};

const FLOW_HUBS: Record<FieldFlow, string> = {
  leads: "/field/leads",
  payments: "/field/payments/new",
  install: "/field/installations",
  hub: "/field/leads",
};

const FLOW_LABELS: Record<FieldFlow, string> = {
  leads: "Leads & KYC",
  payments: "Payment Records",
  install: "My Installations",
  hub: "Field Workspace",
};

export function FieldShell() {
  const online = useOnlineStatus();
  const { pending, syncing, lastSync, sync } = useOfflineQueue(online);
  const [state, setState] = useState<FieldShellState>({
    header: { title: "PAY-GO Field" },
    journey: undefined,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  const headerApi = useMemo(
    () => (header: FieldHeaderState) => {
      setState((s) => ({
        ...s,
        header: {
          ...s.header,
          ...header,
        },
      }));
    },
    [],
  );

  const journeyApi = useMemo(
    () => (journey: FieldJourneyState) => {
      setState((s) => ({ ...s, journey }));
    },
    [],
  );

  function onLogout() {
    clearSession();
    navigate("/login", { replace: true });
  }

  function handleBack() {
    if (!state.journey) return;
    const backTarget = state.journey.backTo || FLOW_HUBS[state.journey.flow];
    navigate(backTarget);
  }

  const hubTabs = useMemo(() => {
    if (!user?.role) return [];
    const tabs = [];
    if (["business_admin", "commercial_officer", "sales_rep"].includes(user.role)) {
      tabs.push({ flow: "leads" as const, label: "Leads", path: "/field/leads" });
    }
    if (["business_admin", "technician", "commercial_officer"].includes(user.role)) {
      tabs.push({ flow: "install" as const, label: "Installations", path: "/field/installations" });
    }
    if (["business_admin", "commercial_officer", "sales_rep"].includes(user.role)) {
      tabs.push({ flow: "payments" as const, label: "Payments", path: "/field/payments/new" });
    }
    return tabs;
  }, [user?.role]);

  const showBack = state.journey && !state.journey.hideBack && state.journey.flow !== "hub";
  const showJourneyBar =
    state.journey && state.journey.flow !== "hub" && state.journey.step !== undefined;
  const stepLabels = state.journey ? FIELD_STEP_LABELS[state.journey.flow] : [];
  const autoCrumbs = state.journey
    ? [
        { label: "Field", to: FLOW_HUBS[state.journey.flow] },
        ...(state.journey.flow !== "hub" ? [{ label: FLOW_LABELS[state.journey.flow] }] : []),
      ]
    : [];
  const crumbs =
    state.header.crumbs && state.header.crumbs.length > 0 ? state.header.crumbs : autoCrumbs;

  return (
    <div className="pg-field-shell">
      <header className="pg-field-header">
        <div className="pg-field-header-left">
          {showBack ? (
            <button
              type="button"
              className="pg-field-back"
              onClick={handleBack}
              aria-label="Go back"
            >
              <IconChevronLeft size={18} />
            </button>
          ) : null}
          <div>
            <div className="pg-field-title">{state.header.title}</div>
            {state.header.subtitle ? (
              <div className="pg-field-subtitle">{state.header.subtitle}</div>
            ) : null}
            {crumbs.length > 0 ? (
              <nav className="pg-field-crumbs" aria-label="Field breadcrumb">
                {crumbs.map((crumb, idx) =>
                  crumb.to ? (
                    <button
                      key={`${crumb.label}-${idx}`}
                      type="button"
                      className="pg-field-crumb"
                      onClick={() => navigate(crumb.to!)}
                    >
                      {crumb.label}
                    </button>
                  ) : (
                    <span key={`${crumb.label}-${idx}`} className="pg-field-crumb current">
                      {crumb.label}
                    </span>
                  ),
                )}
              </nav>
            ) : null}
          </div>
        </div>
        <div className="pg-field-header-actions">
          {!online ? (
            <span className="pg-field-offline-pill">
              <IconOffline size={13} /> offline
            </span>
          ) : null}
          {["system_admin", "business_admin", "commercial_officer", "sales_rep", "technician"].includes(
            user?.role || "",
          ) ? (
            <button
              type="button"
              className="pg-field-logout"
              onClick={() =>
                navigate(
                  user?.role === "technician" || user?.role === "sales_rep" ? "/customers" : "/",
                )
              }
            >
              Office
            </button>
          ) : null}
          <button type="button" className="pg-field-logout" onClick={onLogout}>
            Log out
          </button>
        </div>
      </header>

      {showJourneyBar ? (
        <FieldProcessStepper
          variant="shell"
          ariaLabel={`${FLOW_LABELS[state.journey!.flow]} steps`}
          steps={stepsFromActiveIndex(stepLabels, state.journey?.step ?? 0)}
        />
      ) : null}

      {!online ? (
        <div className="pg-offline-banner" role="status">
          <IconWarning size={14} /> Offline — will sync
          {pending > 0 ? ` (${pending} queued)` : ""}
        </div>
      ) : pending > 0 ? (
        <div className="pg-offline-banner syncing" role="status">
          {syncing ? (
            <>
              <IconSpinner size={14} /> Syncing {pending} queued item{pending === 1 ? "" : "s"}…
            </>
          ) : (
            <>
              <IconWarning size={14} /> {pending} item{pending === 1 ? "" : "s"} pending sync
              <button type="button" className="pg-linklike" onClick={() => void sync()}>
                Sync now
              </button>
            </>
          )}
        </div>
      ) : lastSync && lastSync.succeeded > 0 ? (
        <div className="pg-offline-banner synced" role="status">
          Synced {lastSync.succeeded} queued item{lastSync.succeeded === 1 ? "" : "s"}.
        </div>
      ) : null}

      <main className="pg-field-content">
        <FieldHeaderContext.Provider value={headerApi}>
          <FieldJourneyContext.Provider value={journeyApi}>
            <Outlet />
          </FieldJourneyContext.Provider>
        </FieldHeaderContext.Provider>
      </main>

      {hubTabs.length > 0 ? (
        <nav className="pg-field-hub-nav">
          {hubTabs.map((tab) => {
            const isActive =
              (tab.flow === "leads" && location.pathname.startsWith("/field/leads")) ||
              (tab.flow === "install" && location.pathname.startsWith("/field/installations")) ||
              (tab.flow === "payments" &&
                (location.pathname.startsWith("/field/payments") ||
                  location.pathname.includes("/payment-timeline")));

            return (
              <button
                key={tab.flow}
                type="button"
                className={`pg-hub-nav-tab ${isActive ? "active" : ""}`}
                onClick={() => navigate(tab.path)}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      ) : null}
    </div>
  );
}
